import genQueryParams from '../utils/genQueryParams';

// The Semble API returns plain Semble bookings. The combined appointments list and
// AppointmentCard use the Health Assessment (Lumeon) shape, so bookings are mapped into it.
const toStatus = booking => {
  if (booking.deleted) {
    return 'cancelled';
  }
  // Semble does not report attendance, so a past booking is assumed to have happened.
  return new Date(booking.end) < new Date() ? 'completed' : 'booked';
};

export const toAppointment = booking => {
  const { location } = booking;
  return {
    'appointment-id': booking.id,
    // The page cancels by section id; Semble bookings have no sections, so the id is reused.
    'appointment-section-id': booking.id,
    // Semble bookings carry no appointment type name yet.
    'appointment-type-name': 'Physiotherapy appointment',
    'appointment-start-datetime': booking.start,
    'appointment-end-datetime': booking.end,
    'appointment-locations': location
      ? [
          {
            'location-display-name': location.header || location.name,
            'location-address-line-1': location.address?.address,
            'location-city': location.address?.city,
            'location-postcode': location.address?.postcode,
          },
        ]
      : [],
    // Semble only gives a display name, so it doubles as the id (used as a React key).
    'appointment-practitioners': booking.doctorName
      ? [{ 'practitioner-id': booking.doctorName, 'practitioner-name': booking.doctorName }]
      : [],
    'appointment-status': toStatus(booking),
    'appointment-reference': booking.reference,
    'appointment-video-url': booking.videoUrl,
  };
};

class SemblePhysioBookings {
  static getInstance(accessToken, refresh = async () => {}, logError = async () => {}) {
    if (this.instance === undefined) {
      this.instance = new this();
    }

    this.instance.logError = logError;
    this.instance.refresh = refresh;
    this.instance.token = accessToken;
    return this.instance;
  }

  constructor() {
    this.apimSubscriptionKey = global.APIM_HA_SUBSCRIPTION_KEY;
    this.baseUrl = `${global.APIM_BASE_URL.slice(0, -'booking/'.length)}semble/`;
  }

  // Only the subscription key is sent. The Semble API's CORS policy does not allow
  // Authorization or X-Transaction-Id, so adding either would fail the browser preflight.
  async call(method, endpoint, { params = {}, body = undefined, headers = {} } = {}) {
    const url = `${this.baseUrl}${endpoint}${genQueryParams(params)}`;
    const init = {
      method,
      cache: 'no-store',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'Ocp-Apim-Subscription-Key': this.apimSubscriptionKey,
        ...headers,
      },
    };

    if (['PUT', 'POST', 'PATCH'].includes(method)) {
      init.body = body === undefined ? '' : JSON.stringify(body);
    }

    let response;
    try {
      response = await fetch(url, init);
      if (!response.ok) {
        const error = await response.json();
        throw error;
      }
    } catch (e) {
      this.logError({
        error: 'CUSTOM',
        text: 'Please refresh the page. If the problem persists, please contact our Customer Centre team for help.',
      });
      throw e;
    }

    return response.json();
  }

  // Resolves the patient's Semble ID. There is no lookup endpoint yet (idaasId to Semble
  // patient ID is still to be agreed), so this only works via the local-dev override:
  // append ?semble-patient-id=<id> to the page URL.
  async getSembleId({ idaasId }) {
    const override = new URLSearchParams(window.location.search).get('semble-patient-id');
    if (override) {
      return { sembleId: override };
    }
    throw new Error(`No Semble patient ID lookup available for idaasId ${idaasId}`);
  }

  async getAppointments({ sembleId }) {
    const { appointments = [] } = await this.call(
      'GET',
      `appointments/${encodeURIComponent(sembleId)}`,
    );
    return appointments.map(toAppointment);
  }

  async cancelAppointment({ appointmentId, patientId }) {
    const booking = await this.call(
      'POST',
      `appointments/${encodeURIComponent(appointmentId)}/cancel`,
      { body: { patientId } },
    );
    return toAppointment(booking);
  }
}

export default SemblePhysioBookings;
